import { useMemo } from 'react';
import { useAuthStore } from '@features/authentication/store/authStore';
import { useCurrentRoleContext } from '@features/role-context/hooks/useRoleContext';
import { getConfigForRole } from '../config';
import { RoleBasedConfig } from '../config/routeTypes';
import { UserRole } from '@features/authentication/types';

export const useRouteConfig = (): RoleBasedConfig | null => {
  const { userSession, isAuthenticated } = useAuthStore();
  const { data: currentRoleContext } = useCurrentRoleContext();

  return useMemo(() => {
    if (!isAuthenticated) {
      return null;
    }

    // PRIORITY 1: Use role context if available (role switching active)
    if (currentRoleContext?.roleType) {
      const config = getConfigForRole(currentRoleContext.roleType);

      if (config) {
        return config;
      }
    }

    // PRIORITY 2: Fallback to auth store roles (initial load, no role context)
    if (!userSession?.currentRoles?.length) {
      return null;
    }

    // Get the highest priority role (first in the list)
    // Roles might come as strings from the API, so we handle both cases
    const primaryRole = userSession.currentRoles[0] as UserRole | string;

    const config = getConfigForRole(primaryRole);

    // Log detailed debugging info in development if no config found
    if (!config && process.env.NODE_ENV === 'development') {
      console.error('🎯 useRouteConfig: Failed to find route configuration:', {
        primaryRole,
        roleType: typeof primaryRole,
        allRoles: userSession.currentRoles,
        userEmail: userSession.email,
        currentRoleContext,
      });
    }

    return config || null;
  }, [isAuthenticated, userSession?.currentRoles, userSession?.email, currentRoleContext]);
};

export const useNavigationConfig = () => {
  const config = useRouteConfig();

  return useMemo(() => {
    if (!config) {
      return { topItems: [], bottomItems: [] };
    }

    return {
      topItems: config.navigation.top,
      bottomItems: config.navigation.bottom,
    };
  }, [config]);
};
